import React, { useState } from "react";
import { Modal, Button, message, Typography } from "antd";
import {
  CopyOutlined,
  CheckCircleOutlined,
  MailOutlined,
  KeyOutlined,
  LinkOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { getWebAppUrl } from "@/utils/webAppUrl";
import { humanizeWorkspaceKey } from "@/utils/workspace";
import "./UserCreatedResultModal.css";

const { Text } = Typography;

// Login URL the newly-created user should visit. `createUserByEmail` generates
// a credential set for the dashone web app (the analytics product) in the
// *running environment's* Firebase project — so on stg/dev the account only
// works on that env's web app, not prod. Derive the URL per environment
// instead of hard-coding prod. (dashmote staff sign into this admin portal via
// Google OAuth, which is unrelated to these credentials.)
const DASHONE_LOGIN_URL = `${getWebAppUrl()}/login`;
const SUPPORT_EMAIL = "product@dashmote.com";

// Convert a product_scopes dict (database-format: market_overview /
// market_deepdive / execution_view → array of country codes) into a
// reader-friendly list of (product, countries) pairs. Empty / missing
// scopes are skipped so the modal doesn't show blank lines.
const summarizeProductScopes = (scopes) => {
  if (!scopes || typeof scopes !== "object") return [];
  return Object.entries(scopes)
    .filter(([, countries]) => Array.isArray(countries) && countries.length > 0)
    .map(([product, countries]) => ({
      product: product.replace(/_/g, " "),
      countries: countries.join(", "),
    }));
};

const CreateUserResultModal = ({ open, onClose, user = null }) => {
  const [copiedItems, setCopiedItems] = useState({});

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItems({ ...copiedItems, [key]: true });
      message.success("Copied to clipboard!");

      setTimeout(() => {
        setCopiedItems((prev) => ({ ...prev, [key]: false }));
      }, 2000);
    });
  };

  if (!user) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-3">
          <CheckCircleOutlined className="text-green-500 text-2xl" />
          <div>
            <div className="text-lg font-semibold">User Created Successfully</div>
            <div className="text-sm text-gray-500 font-normal">
              Save the password below. It will not be shown again.
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={600}
      centered
      footer={
        <div className="flex justify-end items-center gap-2">
          <Button type="primary" onClick={onClose} className="bg-blue-600">
            Done
          </Button>
        </div>
      }
      className="user-created-result-modal"
    >
      <div className="py-4">
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-3">
            <KeyOutlined className="text-yellow-600 text-xl mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-yellow-900 mb-1">
                Important: Save the Password
              </div>
              <div className="text-xs text-yellow-700">
                This is the only time the password will be displayed. Make sure
                to copy it now and share it securely with the user.
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-700">
          <div className="font-semibold mb-1 text-gray-800">
            Share these with the user:
          </div>
          <div className="text-xs text-gray-600">
            Login URL, email, password, and which workspaces they have access
            to. They'll need all of it on first sign-in.
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {/* Login URL */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <LinkOutlined className="text-blue-500" />
                <Text className="text-sm font-semibold text-gray-700">
                  Login URL
                </Text>
              </div>
              <Button
                type="text"
                size="small"
                icon={
                  copiedItems["login_url"] ? (
                    <CheckCircleOutlined className="text-green-500" />
                  ) : (
                    <CopyOutlined />
                  )
                }
                onClick={() => copyToClipboard(DASHONE_LOGIN_URL, "login_url")}
              >
                {copiedItems["login_url"] ? "Copied" : "Copy"}
              </Button>
            </div>
            <Text className="text-base font-mono text-gray-900 break-all">
              {DASHONE_LOGIN_URL}
            </Text>
          </div>

          {/* Email */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MailOutlined className="text-blue-500" />
                <Text className="text-sm font-semibold text-gray-700">
                  Email Address
                </Text>
              </div>
              <Button
                type="text"
                size="small"
                icon={
                  copiedItems["email"] ? (
                    <CheckCircleOutlined className="text-green-500" />
                  ) : (
                    <CopyOutlined />
                  )
                }
                onClick={() => copyToClipboard(user.email, "email")}
              >
                {copiedItems["email"] ? "Copied" : "Copy"}
              </Button>
            </div>
            <Text className="text-base font-mono text-gray-900 break-all">
              {user.email}
            </Text>
          </div>

          {/* Password */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <KeyOutlined className="text-blue-600" />
                <Text className="text-sm font-semibold text-gray-700">
                  Generated Password
                </Text>
              </div>
              <Button
                type="text"
                size="small"
                icon={
                  copiedItems["password"] ? (
                    <CheckCircleOutlined className="text-green-500" />
                  ) : (
                    <CopyOutlined />
                  )
                }
                onClick={() =>
                  copyToClipboard(user.generated_password, "password")
                }
              >
                {copiedItems["password"] ? "Copied" : "Copy"}
              </Button>
            </div>
            <Text className="text-lg font-mono font-semibold text-blue-900 break-all">
              {user.generated_password}
            </Text>
          </div>

          {/* Name (if provided) */}
          {user.name && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Text className="text-sm font-semibold text-gray-700 block mb-2">
                Name
              </Text>
              <Text className="text-base text-gray-900">{user.name}</Text>
            </div>
          )}

          {/* Workspaces & product scopes */}
          {user.id_client && Object.keys(user.id_client).length > 0 && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <TeamOutlined className="text-blue-500" />
                <Text className="text-sm font-semibold text-gray-700">
                  Workspaces &amp; access
                </Text>
              </div>
              <div className="flex flex-col gap-3">
                {Object.entries(user.id_client).map(
                  ([workspaceKey, workspaceData]) => {
                    // Pin to an explicit projection of the fields the modal
                    // is allowed to render. If the API response is later
                    // widened with internal metadata under id_client (e.g. a
                    // hashed token, audit fields), this stops it from being
                    // surfaced in the admin's browser by default.
                    const role = workspaceData?.role;
                    const isSuperAdmin = Boolean(
                      workspaceData?.is_super_admin,
                    );
                    const scopes = summarizeProductScopes(
                      workspaceData?.product_scopes,
                    );
                    return (
                      <div
                        key={workspaceKey}
                        className="text-sm text-gray-800"
                      >
                        <div className="font-semibold">
                          {humanizeWorkspaceKey(workspaceKey)}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          Role: {role || "—"}
                          {isSuperAdmin ? " (super admin)" : ""}
                        </div>
                        {scopes.length > 0 ? (
                          <ul className="mt-1 ml-4 text-xs text-gray-700 list-disc">
                            {scopes.map(({ product, countries }) => (
                              <li key={product}>
                                <span className="font-mono">{product}</span>:{" "}
                                {countries}
                              </li>
                            ))}
                          </ul>
                        ) : isSuperAdmin ? (
                          <div className="text-xs text-gray-500 italic mt-1">
                            (workspace-wide access — super admin)
                          </div>
                        ) : (
                          <div className="text-xs text-amber-700 italic mt-1">
                            (no product-scoped permissions set — verify this
                            is intentional before sharing)
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          )}

          {/* User ID */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Text className="text-sm font-semibold text-gray-700">
                User ID
              </Text>
              <Button
                type="text"
                size="small"
                icon={
                  copiedItems["id"] ? (
                    <CheckCircleOutlined className="text-green-500" />
                  ) : (
                    <CopyOutlined />
                  )
                }
                onClick={() => copyToClipboard(user.id_user, "id")}
              >
                {copiedItems["id"] ? "Copied" : "Copy"}
              </Button>
            </div>
            <Text className="text-xs font-mono text-gray-600 break-all">
              {user.id_user}
            </Text>
          </div>
        </div>

        {/* Support contact — escalation path if the user can't get in even
            after we share the credentials above. */}
        <div className="mt-5 pt-4 border-t border-gray-200 text-xs text-gray-500">
          If the user has trouble logging in, ask them to contact{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-blue-600 hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </div>
      </div>
    </Modal>
  );
};

export default CreateUserResultModal;
